import { useQuery } from '@tanstack/react-query';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { usePageHeaderStore } from '../../stores/pageHeader';
import {
  Activity,
  Clock,
  Zap,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  TrendingUp,
  BarChart3,
} from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';

// ============================================
// Types
// ============================================

interface Trace {
  id: string;
  orgId?: string;
  projectId?: string;
  userId?: string;
  requestId?: string;
  type: string;
  status: 'running' | 'success' | 'error' | 'cancelled';
  startedAt: string;
  endedAt?: string;
  durationMs?: number;
  totalSpans: number;
  totalEvents: number;
  totalArtifacts: number;
  llmCalls?: number;
  totalInputTokens?: number;
  totalOutputTokens?: number;
  errorMessage?: string;
  modelName?: string;
  confidence?: number;
  tags?: string[];
}

interface TracesResponse {
  traces: Trace[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
  };
}

// ============================================
// Constants
// ============================================

const STATUS_CONFIG = {
  running: { icon: RefreshCw, color: 'var(--info)', bg: 'var(--info-dim)', label: 'Running', spin: true },
  success: { icon: CheckCircle, color: 'var(--success)', bg: 'var(--success-dim)', label: 'Success', spin: false },
  error: { icon: XCircle, color: 'var(--danger)', bg: 'var(--danger-dim)', label: 'Error', spin: false },
  cancelled: { icon: AlertCircle, color: 'var(--warning)', bg: 'var(--warning-dim)', label: 'Cancelled', spin: false },
};

const TYPE_LABELS: Record<string, string> = {
  generate_current_state: 'Current State',
  generate_future_state: 'Future State',
  generate_change_log: 'Change Log',
  generate_ac: 'Acceptance Criteria',
  generate_wireframe: 'Wireframe',
  rag_query: 'RAG Query',
  sync_request: 'Sync',
  export_jira: 'Jira Export',
  export_slack: 'Slack Export',
  capture_page_context: 'Page Capture',
};

// ============================================
// Component
// ============================================

export function TracesPage() {
  const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
  const [searchParams] = useSearchParams();
  const { setHeader, clearHeader } = usePageHeaderStore();
  
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [confidenceLow, setConfidenceLow] = useState(searchParams.get('confidenceLow') === 'true');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['traces', projectId, typeFilter, statusFilter, confidenceLow, page],
    queryFn: () => {
      const params = new URLSearchParams();
      if (projectId) params.set('projectId', projectId);
      if (typeFilter) params.set('type', typeFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (confidenceLow) params.set('confidenceLow', 'true');
      params.set('page', String(page));
      params.set('limit', '20');
      params.set('sortBy', 'started_at');
      params.set('sortOrder', 'desc');
      
      return api.get<TracesResponse>(`/telemetry/traces?${params.toString()}`);
    },
    refetchInterval: 10000,
  });
  
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<{ name: string; key: string; orgName?: string }>(`/projects/${projectId}`),
    enabled: !!projectId,
  });
  
  useEffect(() => {
    setHeader(
      'Telemetry Traces',
      `${project?.name || 'Project'} (${project?.key || ''}) — Pipeline execution logs`,
      <Activity className="w-5 h-5" style={{ color: 'var(--secondary)' }} />,
      undefined,
      {
        actions: [
          {
            id: 'refresh',
            label: 'Refresh',
            icon: <RefreshCw className="w-4 h-4" />,
            variant: 'secondary',
            onClick: () => refetch(),
          },
        ],
      }
    );
    return () => clearHeader();
  }, [project, setHeader, clearHeader, refetch]);
  
  // Calculate stats
  const stats = useMemo(() => {
    if (!data?.traces) return null;
    
    const total = data.traces.length;
    const success = data.traces.filter(t => t.status === 'success').length;
    const errors = data.traces.filter(t => t.status === 'error').length;
    const avgDuration = data.traces
      .filter(t => t.durationMs)
      .reduce((acc, t) => acc + (t.durationMs || 0), 0) / (total || 1);
    const totalTokens = data.traces.reduce(
      (acc, t) => acc + (t.totalInputTokens || 0) + (t.totalOutputTokens || 0), 
      0
    );
    
    return {
      total,
      successRate: total > 0 ? Math.round((success / total) * 100) : 0,
      errorCount: errors,
      avgDuration: Math.round(avgDuration),
      totalTokens,
    };
  }, [data?.traces]);
  
  const formatDuration = (ms?: number) => {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };
  
  const formatTokens = (tokens?: number) => {
    if (!tokens) return '—';
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}k`;
    return String(tokens);
  };
  
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-card flex items-center justify-center"
                style={{ background: 'var(--primary-dim)' }}
              >
                <BarChart3 className="w-5 h-5" style={{ color: 'var(--primary)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-default">{data?.pagination.total || 0}</p>
                <p className="text-xs text-muted tracking-wider">Total</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-card flex items-center justify-center"
                style={{ background: stats.successRate >= 90 ? 'var(--success-dim)' : stats.successRate >= 70 ? 'var(--warning-dim)' : 'var(--danger-dim)' }}
              >
                <TrendingUp className="w-5 h-5" style={{ color: stats.successRate >= 90 ? 'var(--success)' : stats.successRate >= 70 ? 'var(--warning)' : 'var(--danger)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-default">{stats.successRate}%</p>
                <p className="text-xs text-muted tracking-wider">Success Rate</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-card flex items-center justify-center"
                style={{ background: stats.errorCount > 0 ? 'var(--danger-dim)' : 'var(--surface-2)' }}
              >
                <XCircle className="w-5 h-5" style={{ color: stats.errorCount > 0 ? 'var(--danger)' : 'var(--text-muted)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-default">{stats.errorCount}</p>
                <p className="text-xs text-muted tracking-wider">Errors</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-card flex items-center justify-center"
                style={{ background: 'var(--info-dim)' }}
              >
                <Clock className="w-5 h-5" style={{ color: 'var(--info)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-default">{formatDuration(stats.avgDuration)}</p>
                <p className="text-xs text-muted tracking-wider">Avg Duration</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-card flex items-center justify-center"
                style={{ background: 'var(--warning-dim)' }}
              >
                <Zap className="w-5 h-5" style={{ color: 'var(--warning)' }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-default">{formatTokens(stats.totalTokens)}</p>
                <p className="text-xs text-muted tracking-wider">Tokens Used</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
          className="select w-auto"
        >
          <option value="">All Types</option>
          <option value="generate_current_state">Current State</option>
          <option value="generate_future_state">Future State</option>
          <option value="generate_change_log">Change Log</option>
          <option value="generate_ac">Acceptance Criteria</option>
          <option value="rag_query">RAG Query</option>
          <option value="sync_request">Sync</option>
        </select>
        
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="select w-auto"
        >
          <option value="">All Statuses</option>
          <option value="running">Running</option>
          <option value="success">Success</option>
          <option value="error">Error</option>
          <option value="cancelled">Cancelled</option>
        </select>
        
        <label 
          className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-input transition-colors"
          style={{ background: confidenceLow ? 'var(--warning-dim)' : 'var(--surface-2)' }}
        >
          <input
            type="checkbox"
            checked={confidenceLow}
            onChange={(e) => {
              setConfidenceLow(e.target.checked);
              setPage(1);
            }}
            className="w-4 h-4 rounded accent-primary"
          />
          <span className="text-sm text-default">Low confidence only</span>
        </label>
      </div>
      
      {/* Traces Table */}
      {isLoading ? (
        <div className="card">
          <div className="skeleton h-64 rounded-card" />
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Trace</th>
                <th>Type</th>
                <th>Status</th>
                <th>Duration</th>
                <th>LLM</th>
                <th>Tokens</th>
                <th>Confidence</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {data?.traces.map((trace) => {
                const statusConfig = STATUS_CONFIG[trace.status];
                const StatusIcon = statusConfig.icon;
                
                return (
                  <tr key={trace.id} className="group">
                    <td>
                      <Link
                        to={`/app/organizations/${orgId}/projects/${projectId}/telemetry/${trace.id}`}
                        className="hover:text-primary transition-colors"
                      >
                        <p className="font-mono text-xs text-muted">{trace.id}</p>
                        {trace.requestId && (
                          <p className="text-xs text-subtle mt-0.5">
                            Request: {trace.requestId.slice(0, 12)}...
                          </p>
                        )}
                      </Link>
                    </td>
                    <td>
                      <span className="badge badge-primary">
                        {TYPE_LABELS[trace.type] || trace.type}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <StatusIcon 
                          className={`w-4 h-4 ${statusConfig.spin ? 'animate-spin' : ''}`}
                          style={{ color: statusConfig.color }}
                        />
                        <span className="text-sm text-default">
                          {statusConfig.label}
                        </span>
                      </div>
                      {trace.errorMessage && (
                        <p className="text-xs mt-0.5 line-clamp-1" style={{ color: 'var(--danger)' }}>
                          {trace.errorMessage}
                        </p>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5 text-default">
                        <Clock className="w-3.5 h-3.5 text-subtle" />
                        {formatDuration(trace.durationMs)}
                      </div>
                    </td>
                    <td>
                      {trace.llmCalls ? (
                        <div className="text-default">
                          <span className="text-sm">{trace.llmCalls} call{trace.llmCalls > 1 ? 's' : ''}</span>
                          {trace.modelName && (
                            <p className="text-xs text-muted">{trace.modelName}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-subtle">—</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5 text-default">
                        <Zap className="w-3.5 h-3.5 text-subtle" />
                        {formatTokens((trace.totalInputTokens || 0) + (trace.totalOutputTokens || 0))}
                      </div>
                    </td>
                    <td>
                      {trace.confidence !== undefined ? (
                        <div 
                          className="text-sm font-medium"
                          style={{ 
                            color: trace.confidence >= 0.8 ? 'var(--success)' :
                                   trace.confidence >= 0.6 ? 'var(--warning)' : 'var(--danger)'
                          }}
                        >
                          {Math.round(trace.confidence * 100)}%
                        </div>
                      ) : (
                        <span className="text-subtle">—</span>
                      )}
                    </td>
                    <td className="text-muted text-sm whitespace-nowrap">
                      {new Date(trace.startedAt).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {(!data?.traces || data.traces.length === 0) && (
            <div className="empty-state">
              <Activity className="empty-state-icon" />
              <p className="empty-state-title">No traces found</p>
              <p className="empty-state-text">
                Traces will appear here when pipeline executions occur
              </p>
            </div>
          )}
          
          {/* Pagination */}
          {data?.pagination && data.pagination.total > data.pagination.limit && (
            <div className="flex items-center justify-between p-4 border-t" style={{ borderColor: 'var(--border)' }}>
              <p className="text-muted text-sm">
                Showing {((page - 1) * data.pagination.limit) + 1} - {Math.min(page * data.pagination.limit, data.pagination.total)} of {data.pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn btn-secondary text-sm"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!data.pagination.hasMore}
                  className="btn btn-secondary text-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
